import * as React from 'react';
import { IOrganization, Organization } from '../../models/organization';
import { IOrganizationOrderSource, OrganizationOrderSource } from '../../models/organizationOrderSource';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  Box,
  Button,
  DialogContent, 
  DialogTitle,
  Paper, 
  Switch, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useState, useEffect } from 'react';
import store from '../../store/index';
import { getOrderSourceProperties, getOrganization, upsertOrganization, upsertOrderSourceProperties, getKnownOrderSources } from '../../utilities/api-utils';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}/>
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    alignItems: 'center'
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function OrganizationTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ marginTop: '20px', marginLeft: '20px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `cabinet-tab-${index}`,
    'aria-controls': `cabinet-tabpanel-${index}`,
  };
}

interface OrganizationsDialogProps {
  orgs: IOrganization[],
  setDialog(arg: boolean, update?: boolean): void
}

function OrganizationDialog({ orgs, setDialog }: OrganizationsDialogProps) {
  const [value, setValue] = React.useState(0);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const organizations = orgs;
  const [addingOrg, setAddingOrg] = useState<boolean>(false);
  const [editOrderSources, setEditOrderSources] = useState<Array<OrganizationOrderSource>>([]);
  // Set defaults for new order source
  const sourceInterface: IOrganizationOrderSource = {
    order_source_type: '',
    order_source_display: '',
    digits: 3,
    available: false,
    alphaNumeric: false,
    use_customer_phone: false,
    use_customer_name: false,
    manual_code_entry: false,
    preferred_order: '',
  };
  const [newOrderSource, setNewOrderSource] = useState<OrganizationOrderSource>(sourceInterface);
  const [hasNewOrderSource, setHasNewOrderSource] = useState<boolean>(false);
  const [changedSources, setChangedSources] = useState<Array<string>>([]);
  const [orderSourcesDirty, setOrderSourcesDirty] = useState<boolean>(false);
  const [editOrgName, setEditOrgName] = useState<string>('');
  const [editOrgId, setEditOrgId] = useState<string>('');
  const [editBusinessName, setEditBusinessName] = useState<string>('');
  const [editBusinessPhone, setEditBusinessPhone] = useState<string>('');
  const [editBusinessAddress, setEditBusinessAddress] = useState<string>('');
  const [editBusinessCity, setEditBusinessCity] = useState<string>('');
  const [editBusinessState, setEditBusinessState] = useState<string>('');
  const [editBusinessPostalCode, setEditBusinessPostalCode] = useState<string>('');
  const [editBusinessCountry, setEditBusinessCountry] = useState<string>('');
  const [editUserFirstName, setEditUserFirstName] = useState<string>('');
  const [editUserLastName, setEditUserLastName] = useState<string>('');
  const [editUserEmail, setEditUserEmail] = useState<string>('');
  const [editEnableOrderSms, setEditEnableOrderSms] = useState<boolean>(true);
  const [oloSharedSecret, setOloSharedSecret] = useState<string>('');
  const [oloBrand, setOloBrand] = useState<string>('');
  const [enableOlo, setEnableOlo] = useState<boolean>(false);
  const [brandServices] = useState<string>('uber,doordash,allset,bitesquad,deliverclub,delivery.com,eatstreet,favor,ritual,waitr,zifty,postmates,habitat,ally,drivekindness,google');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [orgNameErrorText, setOrgNameErrorText] = useState<string>('');
  const [businessNameErrorText, setBusinessNameErrorText] = useState<string>('');
  const [businessPhoneErrorText, setBusinessPhoneErrorText] = useState<string>('');
  const [oloSharedSecretErrorText, setOloSharedSecretErrorText] = useState<string>('');
  const [oloBrandErrorText, setOloBrandErrorText] = useState<string>('');
  
  const [hasOrgNameError, setHasOrgNameError] = useState<boolean>(false);
  const [hasBusinessNameError, setHasBusinessNameError] = useState<boolean>(false);
  const [hasBusinessPhoneError, setHasBusinessPhoneError] = useState<boolean>(false);
  const [hasOloSharedSecretError, setHasOloSharedSecretError] = useState<boolean>(false);
  const [hasOloBrandError, setHasOloBrandError] = useState<boolean>(false);

  const toggleOlo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableOlo(event.target.checked);
  };

  const setupOrderSources = async (orgId: string) => {
    setEditOrderSources([]);
    setChangedSources([]);
    const tempEditSources = [];
    // Get all known order sources via API call
    const knownOrderSources = await getKnownOrderSources(store.getState().authorization.cloudKey);

    if (orgId) {
      const sources = await getOrderSourceProperties(store.getState().authorization.cloudKey, orgId);
      for (const key of Object.keys(sources.data)) {
        // Use existing values from organization_order_sources if available
        // otherwise use defaults
        const orgInterface: IOrganizationOrderSource = {
          order_source_type: key,
          order_source_display: sources.data[key].order_source_display || '',
          digits: sources.data[key].digits || 3,
          available: sources.data[key].available || false,
          alphaNumeric: sources.data[key].alphaNumeric || false,
          use_customer_phone: sources.data[key].use_customer_phone || false,
          use_customer_name: sources.data[key].use_customer_name || false,
          manual_code_entry: (typeof sources.data[key].manual_code_entry !== 'undefined')
            ? sources.data[key].manual_code_entry
            : true,
          preferred_order: sources.data[key].preferred_order >= 0 ?
            (+sources.data[key].preferred_order + 1).toString() :
            '1'
        };

        tempEditSources.push(orgInterface);
      }
    }

    // Process each known order source record for display & selection
    for (const source of knownOrderSources.data) {
      if (!tempEditSources.find(x => x.order_source_type === source.name)) {
        const sourceInterface: IOrganizationOrderSource = {
          order_source_type: source.name,
          order_source_display: source.display_name,
          digits: 3,
          available: false,
          alphaNumeric: false,
          use_customer_phone: false,
          use_customer_name: false,
          manual_code_entry: true,
          preferred_order: source.preferred_order >= 0 ?
          (+source.preferred_order + 1).toString() :
          '1'
        };

        const newSource = new OrganizationOrderSource(sourceInterface);

        tempEditSources.push(newSource);
      }
    }

    // Org order sources that do not yet have a display name get one here
    tempEditSources.map(tempSource => {
      if (!tempSource.order_source_display.length) {
        const thisOrderSource = (knownOrderSources.data.find(
          (source: { name: string; }) => source.name === tempSource.order_source_type
        ));

        tempSource.order_source_display = thisOrderSource.display_name;
      }
    });

    tempEditSources.sort( (a, b) => {
      if (a.available > b.available) {
        return -1;
      }

      if (a.available < b.available) {
        return 1;
      }

      return 0;
    });

    setEditOrderSources(tempEditSources);
  };

  const editOrg = async (orgId: string) => {
    setAddingOrg(true);
    // by default, no new order source
    setHasNewOrderSource(false);
    setNewOrderSource(sourceInterface);
    // set initial values for add and edit
    setEditOrgName('');
    setEditBusinessName('');
    setEditBusinessPhone('');
    setEditBusinessAddress('');
    setEditBusinessCity('');
    setEditBusinessState('');
    setEditBusinessPostalCode('');
    setEditBusinessCountry('');
    setEditUserFirstName('');
    setEditUserLastName('');
    setEditUserEmail('');
    setEditEnableOrderSms(true);
    setEnableOlo(false);
    setOloSharedSecret('');
    setOloBrand('');
    setOrgNameErrorText('');
    setBusinessNameErrorText('');
    setBusinessPhoneErrorText('');
    setOloSharedSecretErrorText('');
    setOloBrandErrorText('');
    setHasOrgNameError(false);
    setHasBusinessNameError(false);
    setHasBusinessPhoneError(false);
    setHasOloSharedSecretError(false);
    setHasOloBrandError(false);
    // Update values for this org if orgId exists; otherwise they will remain as default values
    if (orgId.length) {
      setEditOrgId(orgId);
      
      const fetchedOrg = await getOrganization(store.getState().authorization.cloudKey, orgId);
      const concreteOrg = new Organization(fetchedOrg.data[0]);
      
      setEditOrgName(concreteOrg.name);
      setEditBusinessName(concreteOrg.business_name || '');
      setEditBusinessPhone(concreteOrg.business_phone || '');
      setEditBusinessAddress(concreteOrg.business_address || '');
      setEditBusinessCity(concreteOrg.business_city || '');
      setEditBusinessState(concreteOrg.business_state || '');
      setEditBusinessPostalCode(concreteOrg.business_postal_code || '');
      setEditBusinessCountry(concreteOrg.business_country || '');
      setEditUserFirstName(concreteOrg.org_user_first_name || '');
      setEditUserLastName(concreteOrg.org_user_last_name || '');
      setEditUserEmail(concreteOrg.org_user_email || '');
      setOloSharedSecret(concreteOrg.olo_shared_secret || '');
      setOloBrand(concreteOrg.olo_brand || '');
      setEditEnableOrderSms(concreteOrg.enable_order_sms);
      if (concreteOrg?.olo_brand_details?.deliveryServiceSlugsCsv) {
        setEnableOlo(true);
      }
    }
    await setupOrderSources(orgId);
  };

  const validate = () => {
    const invalidDetails = 
      !editOrgName || !editBusinessName || !editBusinessPhone || (enableOlo && (!oloSharedSecret || !oloBrand));

    if (invalidDetails) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  useEffect(() => {
    validate();
  }, [editOrgName, editBusinessName, editBusinessPhone, enableOlo, oloSharedSecret, oloBrand]);

  const upsertOrg = async () => {
    let payload = {
      name: editOrgName,
      business_phone: editBusinessPhone,
      business_name: editBusinessName,
      business_address: editBusinessAddress,
      business_city: editBusinessCity,
      business_state: editBusinessState,
      business_postal_code: editBusinessPostalCode,
      business_country: editBusinessCountry,
      org_user_first_name: editUserFirstName,
      org_user_last_name: editUserLastName,
      org_user_email: editUserEmail,
      enable_order_sms: editEnableOrderSms
    };
    
    if (editOrgId) {
      payload = Object.assign(payload, {
        id: editOrgId
      });
    }

    if (enableOlo) {
      payload = Object.assign(payload, {
        olo_shared_secret: oloSharedSecret,
        olo_brand: oloBrand,
        olo_brand_details: {
          'deliveryServiceSlugsCsv': brandServices,
          'orderingProviderSlugsCsv': brandServices
        }
      });
    } else {
      payload = Object.assign(payload, {
        olo_brand_details: {
          'deliveryServiceSlugsCsv': '',
          'orderingProviderSlugsCsv': ''
        }
      });
    }
    
    const response = await upsertOrganization(store.getState().authorization.cloudKey, payload);
    if (response?.status !== 201 && response?.status !== 200) {
      let detailString = '';
      Object.keys(response.response.data.details).forEach(k => {
        detailString += response.response.data.details[k];
      });
      alert(`${response.response.data.error}:\n${detailString}`);
    } else {
      if (response.data.newKey) {
        const alertMessage = `SAVE THIS KEY. IT WILL NOT BE DISPLAYED AGAIN\n${editOrgName} master key: ${response.data.newKey.key}`;
        alert(alertMessage);
      }
      setDialog(false, true);
    }

    if (orderSourcesDirty) {
      // Pre-process any new order source
      if (hasNewOrderSource) {
        // If user only entered order source name or order source display,
        // use that value to set the other
        if (newOrderSource.order_source_type && !newOrderSource.order_source_display) {
          newOrderSource.order_source_display = newOrderSource.order_source_type;
        }

        if (newOrderSource.order_source_display && !newOrderSource.order_source_type) {
          // strip spaces and set to lowercase
          newOrderSource.order_source_type = newOrderSource.order_source_display.replace(' ', '').toLowerCase();
        }

        changedSources.push(newOrderSource.order_source_type);
        editOrderSources.push(newOrderSource);
      }

      setOrderSourcesDirty(false);
      for (const changedSource of changedSources) {
        const source = editOrderSources.find(x => x.order_source_type === changedSource);
        const response = await upsertOrderSourceProperties(store.getState().authorization.cloudKey, editOrgId, source!.order_source_type, OrganizationOrderSource.payload(source!));
        if (response?.status !== 201) {
          let detailString = '';
          Object.keys(response.response.data.details).forEach(k => {
            detailString += response.response.data.details[k];
          });
          alert(`${response.response.data.error}:\n${detailString}`);
        }
      }
    }
  };

  const onSourceSelectChanged = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const editAvailable = (source: OrganizationOrderSource, isAvailable: boolean) => {
    setEditOrderSources((existingSources) =>
      existingSources.map((originalSource) => ({
        ...originalSource,
        available: originalSource.order_source_type === source.order_source_type ? isAvailable : originalSource.available
      }))
    );

    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }

    setOrderSourcesDirty(true);
  };

  const editPreferredOrder = (source: OrganizationOrderSource, preferredOrder: string) => {
    const conversion = preferredOrder;

    setEditOrderSources((existingSources) =>
      existingSources.map((originalSource) => ({
        ...originalSource,
        preferred_order: originalSource.order_source_type === source.order_source_type ? conversion : originalSource.preferred_order
      }))
    );

    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }

    setOrderSourcesDirty(true);
  };

  const editDigits = (source: OrganizationOrderSource, digits: string) => {
    const conversion = parseInt(digits);
    if (Number.isNaN(conversion) || conversion < 1) {
      alert('Digits must be a whole number greater than 0');
    } else {
      setEditOrderSources((existingSources) =>
        existingSources.map((originalSource) => ({
          ...originalSource,
          digits: originalSource.order_source_type === source.order_source_type ? conversion : originalSource.digits
        }))
      );
      if (!changedSources.find(x => x === source.order_source_type)) {
        changedSources.push(source.order_source_type);
      }
      setOrderSourcesDirty(true);
    }
  };

  const editAlphaNumeric = (source: OrganizationOrderSource, alphaNumeric: boolean) => {
    setEditOrderSources((existingSources) =>
    existingSources.map((originalSource) => ({
        ...originalSource,
        alphaNumeric: originalSource.order_source_type === source.order_source_type ? alphaNumeric : originalSource.alphaNumeric
      }))
    );
    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }
    setOrderSourcesDirty(true);
  };

  const editUseCustomerPhone = (source: OrganizationOrderSource, useCustomerPhone: boolean) => {
    setEditOrderSources((existingSources) =>
    existingSources.map((originalSource) => ({
        ...originalSource,
        use_customer_phone: originalSource.order_source_type === source.order_source_type ? useCustomerPhone : originalSource.use_customer_phone
      }))
    );
    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }
    setOrderSourcesDirty(true);
  };

  const editUseCustomerName = (source: OrganizationOrderSource, useCustomerName: boolean) => {
    setEditOrderSources((existingSources) =>
    existingSources.map((originalSource) => ({
        ...originalSource,
        use_customer_name: originalSource.order_source_type === source.order_source_type ? useCustomerName : originalSource.use_customer_name
      }))
    );
    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }
    setOrderSourcesDirty(true);
  };

  const editManualCodeEntry = (source: OrganizationOrderSource, manualCodeEntry: boolean) => {
    setEditOrderSources((existingSources) =>
    existingSources.map((originalSource) => ({
        ...originalSource,
        manual_code_entry: originalSource.order_source_type === source.order_source_type ? manualCodeEntry : originalSource.manual_code_entry
      }))
    );
    if (!changedSources.find(x => x === source.order_source_type)) {
      changedSources.push(source.order_source_type);
    }
    setOrderSourcesDirty(true);
  };

  const editOrderSourceName = (orderSourceName: string) => {
    newOrderSource.order_source_type = orderSourceName;
    editNewAvailable(true);
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editOrderSourceDisplay = (orderSourceDisplay: string) => {
    newOrderSource.order_source_display = orderSourceDisplay;
    editNewAvailable(true);
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewAvailable = (isAvailableChecked: boolean) => {
    newOrderSource.available = isAvailableChecked;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewPreferredOrder = (preferredOrder: string) => {
    newOrderSource.preferred_order = preferredOrder;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewDigits = (orderSourceDigits: string) => {
    newOrderSource.digits = parseInt(orderSourceDigits);

    if (Number.isNaN(newOrderSource.digits) || newOrderSource.digits < 1) {
      alert('Digits must be a whole number greater than 0');
    } else {
      setNewOrderSource(newOrderSource);
      setOrderSourcesDirty(true);
    }
  };

  const editNewAlphaNumeric = (isAlphanumericChecked: boolean) => {
    newOrderSource.alphaNumeric = isAlphanumericChecked;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewUseCustomerPhone = (isUseCustomerPhoneChecked: boolean) => {
    newOrderSource.use_customer_phone = isUseCustomerPhoneChecked;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewUseCustomerName = (isUseCustomerNameChecked: boolean) => {
    newOrderSource.use_customer_name = isUseCustomerNameChecked;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const editNewManualCodeEntry = (isManualCodeEntryChecked: boolean) => {
    newOrderSource.manual_code_entry = isManualCodeEntryChecked;
    setNewOrderSource(newOrderSource);
    setOrderSourcesDirty(true);
  };

  const addNewOrderSource = () => {
    setHasNewOrderSource(true);
  };

  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px'
          }}>
          Organizations
        </div>
        <Button 
          sx={{
            borderRadius: '5px',
            width: 'auto',
            height: 'auto',
            padding: '0',
            minWidth: '0',
            '&:hover': {
              opacity: '.5',
            }
          }}
          onClick={() => {
            setDialog(false);
            setAddingOrg(false);
          }}>
          <CloseIcon
            sx={{
              padding: '0',
              fontSize: '2rem',
              color: '#121F35',
              width: '40px',
              height: '40px',
            }} />
        </Button>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '650px',
        }}>
        {!addingOrg ?
          <Box
            id='data-table'
            sx={{
              height: '90%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
            <TableContainer component={Paper} sx={{maxHeight: '570px'}}>
              <Table
                sx={{minWidth: '600px'}}>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>Name</TableCell>
                    <TableCell align='left'>Phone</TableCell>
                    <TableCell align='center'>SMS Enabled</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizations.map((org, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {org.name}
                      </TableCell>
                      <TableCell>
                        {org.business_phone}
                      </TableCell>
                      <TableCell align='center'>
                        {org.enable_order_sms ? 'Enabled' : 'Disabled'}
                      </TableCell>
                      <TableCell align='center' sx={{padding: '0'}}>
                        <Button
                          variant='contained'
                          onClick={() => editOrg(org.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              variant='contained'
              sx={{
                fontSize: '24px',
                marginBottom: 'auto',
                marginTop: 'auto'
              }}
              onClick={() => editOrg('')}>
              Add Organization
            </Button>
          </Box>
          :
          <Box
            id='add-org'
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column'
            }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="Organization Details" {...a11yProps(0)} />
                <Tab label="Organization Order Sources" {...a11yProps(1)} />
              </Tabs>
            </Box>
            <OrganizationTabPanel
              value={value}
              index={0}>
              <Box 
                sx={{
                  height: '500px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      inputProps={{ maxLength: 24 }}
                      sx={{
                        marginTop: '10px',
                        width: '47%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      disabled
                      value={editOrgId}
                      label='ID' />
                    <TextField
                      error={hasOrgNameError}
                      helperText={orgNameErrorText}
                      inputProps={{ maxLength: 36 }}
                      sx={{
                        marginTop: '10px',
                        width: '47%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      required
                      value={editOrgName}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setOrgNameErrorText('Organization Name is required');
                          setHasOrgNameError(true);
                        } else {
                          setOrgNameErrorText('');
                          setHasOrgNameError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setEditOrgName(e.target.value);
                      }}
                      label='Name' />
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      error={hasBusinessNameError}
                      helperText={businessNameErrorText}
                      inputProps={{ maxLength: 50 }}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      required
                      value={editBusinessName}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBusinessNameErrorText('Business Name is required');
                          setHasBusinessNameError(true);
                        } else {
                          setBusinessNameErrorText('');
                          setHasBusinessNameError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setEditBusinessName(e.target.value);
                      }}
                      label='Business Name' />

                    <TextField
                      inputProps={{ maxLength: 255 }}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      value={editBusinessAddress}
                      onChange = {(e) => {
                        setEditBusinessAddress(e.target.value);
                      }}
                      label='Business Address' />
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      inputProps={{ maxLength: 255 }}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      value={editBusinessCity}
                      onChange = {(e) => {
                        setEditBusinessCity(e.target.value);
                      }}
                      label='Business City' />

                    <TextField
                      inputProps={{ maxLength: 25 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      value={editBusinessState}
                      onChange = {(e) => {
                        setEditBusinessState(e.target.value);
                      }}
                      label='Business State' />

                    <TextField
                      inputProps={{ maxLength: 25 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      value={editBusinessPostalCode}
                      onChange = {(e) => {
                        setEditBusinessPostalCode(e.target.value);
                      }}
                      label='Business Postal Code' />
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      inputProps={{ maxLength: 100 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      value={editBusinessCountry}
                      onChange = {(e) => {
                        setEditBusinessCountry(e.target.value);
                      }}
                      label='Business Country' />

                    <TextField
                      error={hasBusinessPhoneError}
                      helperText={businessPhoneErrorText}
                      inputProps={{ maxLength: 15 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      value={editBusinessPhone}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBusinessPhoneErrorText('BusinessPhone is required');
                          setHasBusinessPhoneError(true);
                        } else {
                          setBusinessPhoneErrorText('');
                          setHasBusinessPhoneError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setEditBusinessPhone(e.target.value);
                      }}
                      required
                      label='Business Phone Number' />

                    <TextField
                      inputProps={{ maxLength: 100 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      value={editUserFirstName}
                      onChange = {(e) => {
                        setEditUserFirstName(e.target.value);
                      }}
                      label='User First Name' />

                    <TextField
                      inputProps={{ maxLength: 100 }}
                      sx={{
                        width: '22.9%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      value={editUserLastName}
                      onChange = {(e) => {
                        setEditUserLastName(e.target.value);
                      }}
                      label='User Last Name' />
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      inputProps={{ maxLength: 255 }}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      value={editUserEmail}
                      onChange = {(e) => {
                        setEditUserEmail(e.target.value);
                      }}
                      label='User Email' />
                    <Box
                      sx={{
                        width: '47%',
                        display: 'flex',
                        flexDirection: 'row',
                        marginLeft: '30px',
                        marginTop: '10px'
                      }}>
                        <Box
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row'
                          }}>
                          <Typography
                            sx={{
                              marginTop: '7px',
                              marginRight: '30px'
                            }}>Olo Enabled</Typography>
                          <Switch
                            checked={enableOlo}
                            onChange={toggleOlo} />
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            marginLeft: '20px'
                          }}>
                            <Typography
                              sx={{
                                marginTop: '7px',
                                marginRight: '30px'
                              }}>Enable SMS</Typography>
                            <Switch
                              checked={editEnableOrderSms}
                              onChange = {(e) => {
                                setEditEnableOrderSms(e.target.checked);
                              }} />
                        </Box>
                      </Box>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      display: enableOlo ? 'flex' : 'none',
                      flexDirection: 'row'
                    }}>
                    <TextField
                      error={hasOloSharedSecretError}
                      helperText={oloSharedSecretErrorText}
                      inputProps={{ maxLength: 256 }}
                      disabled={!enableOlo}
                      required={enableOlo}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginRight: '15px'
                      }}
                      label='Olo Shared Secret'
                      value={oloSharedSecret}
                      onBlur = {(e) => {
                        if (!e.target.value && enableOlo) {
                          setOloSharedSecretErrorText('Olo Shared Secret is required');
                          setHasOloSharedSecretError(true);
                        } else {
                          setOloSharedSecretErrorText('');
                          setHasOloSharedSecretError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setOloSharedSecret(e.target.value);
                      }} />
                    <TextField
                      error={hasOloBrandError}
                      helperText={oloBrandErrorText}
                      inputProps={{ maxLength: 36 }}
                      disabled={!enableOlo}
                      required={enableOlo}
                      sx={{
                        width: '47%',
                        marginBottom: '20px',
                        marginLeft: '15px'
                      }}
                      label='Olo Brand'
                      value={oloBrand}
                      onBlur = {(e) => {
                        if (!e.target.value && enableOlo) {
                          setOloBrandErrorText('Olo Brand is required');
                          setHasOloBrandError(true);
                        } else {
                          setOloBrandErrorText('');
                          setHasOloBrandError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setOloBrand(e.target.value);
                      }} />
                  </Box>
              </Box>
            </OrganizationTabPanel>
            <OrganizationTabPanel
              value={value}
              index={1}>
              <Box 
                sx={{
                  height: '500px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <List style={{maxHeight: '95%', overflow: 'auto'}}>
                  {editOrderSources.map((source, index) => (
                  <Accordion
                    disableGutters={true}
                    expanded={expanded === source.order_source_type}
                    key={index}
                    onChange={onSourceSelectChanged(source.order_source_type)} >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}>
                      <Typography>{source.order_source_display}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FormGroup>
                        <Box>
                          <FormControlLabel control={
                            <Checkbox checked={source.available} onChange={(e) => {
                              editAvailable(source, e.target.checked);
                            }} />
                          } label='Available' />
                          <TextField
                          label="Preferred Order"
                          type="number"
                          sx={{
                            marginBottom: '15px',
                            width: '210px'
                          }}
                          helperText="Leave empty for default order"
                          value={source.available ? source.preferred_order : ''}
                          onChange={(e) => {
                            editPreferredOrder(source, e.target.value);
                          }} />
                        </Box>
                        <TextField
                          sx={{
                            width: '110px',
                            marginBottom: '15px',
                            marginTop: '15px'
                          }}
                          label='Digits'
                          type="number"
                          value={source.digits}
                          onChange={(e) => {
                            editDigits(source, e.target.value);
                          }} />
                        <FormControlLabel control={
                          <Checkbox checked={source.alphaNumeric} onChange={(e) => {
                            editAlphaNumeric(source, e.target.checked);
                          }} />
                        } label='Alphanumeric' />
                        <FormControlLabel control={
                          <Checkbox checked={source.use_customer_phone} onChange={(e) => {
                            editUseCustomerPhone(source, e.target.checked);
                          }} />
                        } label='Use Customer Phone' />
                        <FormControlLabel control={
                          <Checkbox checked={source.use_customer_name} onChange={(e) => {
                            editUseCustomerName(source, e.target.checked);
                          }} />
                        } label='Use Customer Name' />
                        <FormControlLabel control={
                          <Checkbox checked={source.manual_code_entry} onChange={(e) => {
                            editManualCodeEntry(source, e.target.checked);
                          }} />
                        } label='Manual Code Entry' />
                      </FormGroup>
                    </AccordionDetails>
                  </Accordion>
                  ))}
                  <Box>
                    {hasNewOrderSource &&
                      <Accordion
                        disableGutters={true}
                        expanded={hasNewOrderSource}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}>
                          <Typography>{newOrderSource.order_source_display}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FormGroup>
                            <TextField
                              sx={{
                                width: '50%',
                                marginBottom: '15px'
                              }}
                              label='Order Source Name'
                              defaultValue={newOrderSource.order_source_type}
                              onChange={(e) => {
                                editOrderSourceName(e.target.value);
                              }} />
                            <TextField
                              sx={{
                                width: '50%',
                                marginBottom: '15px'
                              }}
                              label='Order Source Display'
                              defaultValue={newOrderSource.order_source_display}
                              onChange={(e) => {
                                editOrderSourceDisplay(e.target.value);
                              }} />
                            <Box>
                              <FormControlLabel control={
                                <Checkbox checked={newOrderSource.available} onChange={(event) => {
                                  editNewAvailable(event.target.checked);
                                }} />
                              } label='Available' />
                              <TextField
                                label="Preferred Order"
                                type="number"
                                sx={{
                                  marginBottom: '15px',
                                  width: '210px'
                                }}
                                helperText="Leave empty for default order"
                                defaultValue={newOrderSource.preferred_order}
                                onChange={(e) => {
                                  editNewPreferredOrder(e.target.value);
                                }} />
                            </Box>
                            <TextField
                              sx={{
                                width: '110px',
                                marginBottom: '15px'
                              }}
                              label='Digits'
                              type="number"
                              defaultValue={newOrderSource.digits}
                              onChange={(e) => {
                                editNewDigits(e.target.value);
                              }} />
                            <FormControlLabel control={
                              <Checkbox value={newOrderSource.alphaNumeric} onChange={(event) => {
                                editNewAlphaNumeric(event.target.checked);
                              }} />
                            } label='Alphanumeric' />
                            <FormControlLabel control={
                              <Checkbox value={newOrderSource.use_customer_phone} onChange={(event) => {
                                editNewUseCustomerPhone(event.target.checked);
                              }} />
                            } label='Use Customer Phone' />
                            <FormControlLabel control={
                              <Checkbox value={newOrderSource.use_customer_name} onChange={(event) => {
                                editNewUseCustomerName(event.target.checked);
                              }} />
                            } label='Use Customer Name' />
                            <FormControlLabel control={
                              <Checkbox value={newOrderSource.manual_code_entry} onChange={(event) => {
                                editNewManualCodeEntry(event.target.checked);
                              }} />
                            } label='Manual Code Entry' />
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    }
                    {!hasNewOrderSource &&
                      <Button 
                        variant='contained'
                        sx={{
                          fontSize: '24px',
                          display: 'grid',
                          maxWidth: 'fit-content',
                          margin: 'auto'
                        }}
                        onClick={() => addNewOrderSource()}>
                        Add Order Source
                      </Button>
                    }
                  </Box>
                </List>
              </Box>
            </OrganizationTabPanel>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'auto',
                marginTop: 'auto',
                width: '100%'
              }}>
              <Button 
                variant='contained'
                sx={{
                  fontSize: '24px',
                  backgroundColor: '#BDBDBD',
                  '&: hover': {
                    backgroundColor: '#FE2E26'
                  }
                }}
                onClick={() => setAddingOrg(false)}>
                Cancel
              </Button>
              <Button 
                variant='contained'
                disabled={!isFormValid}
                onClick={async() => { await upsertOrg(); }}
                sx={{
                  fontSize: '24px'
                }}>
                Save
              </Button>
            </Box>
          </Box>
        }
      </DialogContent>
    </Box>
  );
}

export default OrganizationDialog;
